// Supplier feeds frequently transpose width and height. We never silently
// "correct" numbers — we detect implausible values and stop asserting them.

const SEATING_OR_TABLE =
  /\b(sofa|sectional|loveseat|couch|settee|bench|ottoman|chaise|table|desk|dresser|sideboard|buffet|credenza|console|nightstand|bed)\b/i;

const NOT_FURNITURE =
  /\b(lamps?|lights?|lighting|sconces?|chandeliers?|pendants?|lanterns?|mirrors?|vases?|planters?|clocks?|frames?|sculptures?|figurines?|statues?|candle\w*|shades?|fans?)\b/i;

export interface ParsedDims {
  width?: number;
  depth?: number;
  height?: number;
}

function num(value?: string): number | undefined {
  if (!value) return undefined;
  const n = parseFloat(value);
  return Number.isFinite(n) && n > 0 ? n : undefined;
}

/** Pieces that are often taller than they are wide: nightstands, side tables, chests. */
const TALL_OK =
  /\b(nightstands?|bedside|end tables?|side tables?|accent tables?|bar tables?|pub tables?|counter|bistro|dressers?|chests?|bookcases?|cabinets?|tower|plant stands?|pedestal)\b/i;

/**
 * A sofa, sectional, bench or table that is taller than it is wide is
 * implausible — the feed has almost certainly swapped the axes. Nightstands,
 * side tables and chests are routinely taller than wide (a 23.5" W × 25" H
 * nightstand is normal), so only the depth rule applies to them.
 */
export function areDimensionsPlausible(
  dims: Record<string, string> | null | undefined,
  title: string
): boolean {
  if (!dims) return true;
  if (!SEATING_OR_TABLE.test(title || "")) return true;
  // "Table Lamp", "Tray Table Floor Lamp", "Desk Mirror": the furniture word
  // qualifies a tall object, so the width-over-height rule does not apply.
  if (NOT_FURNITURE.test(title || "")) return true;

  const width = num(dims["Width"]) ?? num(dims["Length"]);
  const height = num(dims["Height"]);
  const depth = num(dims["Depth"]);

  if (width && height && height > width * 1.05 && !TALL_OK.test(title || "")) return false;
  // A depth greater than the width is also suspect for these categories.
  if (width && depth && depth > width * 1.05) return false;
  return true;
}

/**
 * A title that leads with a size (113" Sofa…) names one of the item's axes.
 * If none of the parsed axes is that size, the feed is inconsistent. Stools
 * and bar chairs lead with the seat height, so that counts as agreement too.
 */
export function titleAgreesWithDims(title: string | undefined, dims: Record<string, string> | null | undefined): boolean {
  const lead = title?.match(/^(\d+\.?\d*)\s*"/);
  if (!lead || !dims) return true;
  const t = parseFloat(lead[1]);
  const keys = ["Width", "Depth", "Height", "Length", "Seat Height"];
  const axes = keys.map((k) => parseFloat(dims[k] ?? "")).filter((n) => n > 0);
  if (!axes.length) return true;
  if (/\b(stools?|bar chairs?|counter chairs?)\b/i.test(title || "") && !dims["Seat Height"]) return true;
  return axes.some((n) => Math.abs(n - t) <= Math.max(1, t * 0.05));
}

export const DIMENSION_VERIFY_NOTICE =
  "The supplier's measurements for this item don't match its title. Email hello@loftloom.com before ordering and we'll check the listing with you.";

/** Shown where the supplier's one set of measurements may belong to another size (sizeFactsApply). */
export const OTHER_SIZE_NOTICE =
  "This piece comes in more than one size, and the supplier gives one set of measurements that may be for another size than the one selected. Go by the size on the option you choose, or email hello@loftloom.com and we'll confirm it before you order.";

const NUM = "(\\d+(?:\\.\\d+)?)";
const UNIT = "(?:[-\\s]?(?:inch(?:es)?|in\\.?|\"|”))";
const AXIS: Record<string, string> = { W: "Width", D: "Depth", H: "Height", L: "Length" };

const fmt = (n: string) => `${parseFloat(n)}"`;

/**
 * The supplier's descriptions carry two sets of numbers: the item's own
 * ("Item Depth: 50.00 Inches", "43-inch H x 113-inch W x 50-inch D") and its
 * shipping carton's ("Shipping Size (Inches.): 43.00 W x 61.00 D x 38.00 H",
 * "Shipping Weight: 302.00 Pounds"). Only the item's numbers may be shown.
 */
export function stripShippingSpecs(desc: string): string {
  return (desc || "")
    // "Shipping Size (Inches.): 43.00 W x 61.00 D x 38.00 H"
    .replace(/Shipping\s+Size[^:]*:\s*[\d.]+\s*[A-Z]?(?:\s*x\s*[\d.]+\s*[A-Z]?)*/gi, " ")
    // "Shipping Weight: 302.00 Pounds", "Shipping Depth Carton 1: 61.00"
    .replace(/Shipping\s+(?:Weight|Height|Width|Depth|Length)[^:]{0,20}:\s*[\d.]+(?:\s*(?:Pounds|lbs?|Inches|in))?/gi, " ")
    .replace(/(?:Package|Carton|Box)\s+(?:Weight|Height|Width|Depth|Length|Size|Dimensions?)[^:]{0,20}:\s*[^A-Za-z]*/gi, " ");
}

/**
 * The item's own dimensions (and weight / seat height when stated), never the
 * shipping carton's. Order of trust: a size in the variant title, the
 * supplier's "Item Width/Depth/Height/Length/Weight" fields, the item sentence
 * ("43-inch H x 113-inch W x 50-inch D"), then generic labels with every
 * shipping clause removed. Returns null when nothing parseable exists.
 */
export function extractItemDims(
  description?: string,
  variantTitle?: string
): Record<string, string> | null {
  const out: Record<string, string> = {};
  const vt = variantTitle?.match(/(\d+\.?\d*)\s*"?\s*x\s*(\d+\.?\d*)\s*"?\s*x\s*(\d+\.?\d*)/i);
  if (vt) {
    out.Width = fmt(vt[1]); out.Depth = fmt(vt[2]); out.Height = fmt(vt[3]);
  }

  const raw = description || "";
  // 1. Explicit item fields.
  for (const axis of ["Width", "Depth", "Height", "Length"]) {
    if (out[axis]) continue;
    const m = raw.match(new RegExp(`Item\\s+${axis}\\s*:\\s*${NUM}`, "i"));
    if (m) out[axis] = fmt(m[1]);
  }
  const iw = raw.match(new RegExp(`Item\\s+Weight\\s*:\\s*${NUM}`, "i"));
  if (iw) out.Weight = `${parseFloat(iw[1])} lbs`;

  const clean = stripShippingSpecs(raw);

  // 2. The item sentence, in any axis order: "43-inch H x 113-inch W x 50-inch D".
  if (!out.Width && !out.Depth && !out.Height) {
    const part = `${NUM}${UNIT}?\\s*([WDHL])\\b`;
    const m = clean.match(new RegExp(`${part}\\s*x\\s*${part}(?:\\s*x\\s*${part})?`, "i"));
    if (m) {
      for (let i = 1; i < m.length; i += 2) {
        if (m[i] && m[i + 1]) out[AXIS[m[i + 1].toUpperCase()]] ??= fmt(m[i]);
      }
    }
  }

  // 3. Generic labels, shipping clauses already removed.
  for (const axis of ["Width", "Depth", "Height"]) {
    if (out[axis]) continue;
    const m = clean.match(new RegExp(`(?<!Seat\\s)${axis}\\s*[:\\s]\\s*${NUM}`, "i"));
    if (m) out[axis] = fmt(m[1]);
  }
  if (!out.Width && !out.Length) {
    const m = clean.match(new RegExp(`Length\\s*[:\\s]\\s*${NUM}`, "i"));
    if (m) out.Length = fmt(m[1]);
  }
  if (!out.Weight) {
    const m = clean.match(new RegExp(`\\bWeight\\s*:\\s*${NUM}\\s*(?:lbs?|pounds?)`, "i"));
    if (m) out.Weight = `${parseFloat(m[1])} lbs`;
  }
  const seat = clean.match(new RegExp(`Seat\\s+Height[^:\\d]{0,20}[:\\s]\\s*${NUM}`, "i"));
  if (seat) out["Seat Height"] = fmt(seat[1]);

  return Object.keys(out).length ? out : null;
}

const FLOOR_GOODS_TYPE = /^(?:area rugs|outdoor rugs|bath rugs|doormats|rug pads)$/i;
const FLOOR_GOODS_TITLE = /\b(?:rugs?|rug\s+pads?|door\s?mats?|bath\s+mats?)\b/i;

export interface FlatSize {
  short: number;
  long: number;
  thickness?: number;
}

/**
 * Rugs, doormats and rug pads lie flat, and the supplier files their two floor
 * dimensions under whichever axes it likes: "Item Height: 120" on a 10' x 14'
 * rug, while on another the thickness is the "Height". Printed as given, a rug
 * was 120 inches "high". So for these the axes are not used at all: the two
 * large numbers are the size, the small one the thickness. Null for anything
 * that is not a flat floor piece — furniture keeps its width, depth and height.
 */
export function flatFloorSize(
  dims: Record<string, string> | null | undefined,
  title?: string,
  productType?: string
): FlatSize | null {
  if (!dims) return null;
  if (!FLOOR_GOODS_TYPE.test((productType || "").trim()) && !FLOOR_GOODS_TITLE.test(title || "")) return null;
  const sides = [num(dims["Width"]) ?? num(dims["Length"]), num(dims["Depth"]), num(dims["Height"])]
    .filter((n): n is number => n !== undefined)
    .sort((a, b) => a - b);
  if (sides.length === 3 && sides[0] <= 3 && sides[1] >= 12) return { short: sides[1], long: sides[2], thickness: sides[0] };
  if (sides.length === 2 && sides[0] >= 12) return { short: sides[0], long: sides[1] };
  return null;
}

/**
 * Overall width/depth/height only (no weight), for the details panel. A flat
 * floor piece (pass its title and product type) comes back as Width × Length.
 */
export function extractOverallDims(
  description?: string,
  variantTitle?: string,
  title?: string,
  productType?: string
): Record<string, string> | null {
  const all = extractItemDims(description, variantTitle);
  if (!all) return null;
  const flat = flatFloorSize(all, title, productType);
  if (flat) return { Width: `${flat.short}"`, Length: `${flat.long}"` };
  const out: Record<string, string> = {};
  if (all.Width ?? all.Length) out.Width = (all.Width ?? all.Length)!;
  if (all.Depth) out.Depth = all.Depth;
  if (all.Height) out.Height = all.Height;
  return Object.keys(out).length ? out : null;
}
